import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, collection, query, where, onSnapshot, addDoc,
  getDocs, doc, getDoc, updateDoc, serverTimestamp
} from '../../firebase/config';
import {
  Send, User, Search, MoreVertical, ChevronLeft,
  MessageCircle, Check, CheckCheck
} from 'lucide-react';

// ============================================================
// Helpers (outside component so they're stable)
// ============================================================
const sameDay = (a, b) => {
  if (!a || !b) return false;
  const da = a.toDate ? a.toDate() : new Date(a);
  const db_ = b.toDate ? b.toDate() : new Date(b);
  return (
    da.getFullYear() === db_.getFullYear() &&
    da.getMonth() === db_.getMonth() &&
    da.getDate() === db_.getDate()
  );
};

const formatDayLabel = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today - target) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-ZA', { weekday: 'long' });
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
};

const formatRelative = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
};

// ============================================================
// ChatThread — defined OUTSIDE ChatPage so it doesn't remount
// ============================================================
const ChatThread = ({
  activeChat,
  activeMessages,
  newMessage,
  setNewMessage,
  sending,
  sendMessage,
  inputRef,
  messagesEndRef,
  onBack,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Header */}
      <div className="p-4 border-b border-gray-50 flex items-center gap-3 bg-white sticky top-0 z-10">
        {onBack && (
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] hover:bg-[#FBE1E8] transition-colors shrink-0"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="w-10 h-10 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] shrink-0 overflow-hidden">
          {activeChat.professionalImage ? (
            <img src={activeChat.professionalImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <User size={18} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 text-sm truncate">
            {activeChat.professionalName}
          </h3>
          <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            Beauty Professional
          </p>
        </div>
        <button className="w-9 h-9 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] shrink-0">
          <MoreVertical size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FCF8F9]">
        {activeMessages.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-3">
              <MessageCircle size={24} className="text-[#EFA9B6]" />
            </div>
            <h3 className="font-bold text-gray-900 text-sm mb-1">Start the conversation</h3>
            <p className="text-gray-400 text-xs">
              Say hi to {activeChat.professionalName.split(' ')[0]}
            </p>
          </div>
        ) : (
          activeMessages.map((msg, idx) => {
            const mine = msg.sender === 'client';
            const prevMsg = activeMessages[idx - 1];
            const showDate = !prevMsg || !sameDay(prevMsg.createdAt, msg.createdAt);
            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest py-2">
                    {formatDayLabel(msg.createdAt)}
                  </div>
                )}
                <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                      mine
                        ? 'bg-[#010101] text-white rounded-br-md'
                        : 'bg-white text-gray-900 border border-gray-100 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm leading-relaxed break-words">{msg.text}</p>
                    <div className={`flex items-center gap-1 mt-1 ${mine ? 'justify-end' : 'justify-start'}`}>
                      <span className={`text-[10px] ${mine ? 'text-white/60' : 'text-gray-400'}`}>
                        {formatTime(msg.createdAt)}
                      </span>
                      {mine && (
                        msg.read
                          ? <CheckCheck size={10} className="text-white/60" />
                          : <Check size={10} className="text-white/60" />
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-50 bg-white shrink-0">
        <div className="flex gap-2 items-end">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="flex-1 bg-[#FCF8F9] border border-gray-100 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFA9B6]"
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            className="w-11 h-11 bg-[#010101] text-white rounded-2xl hover:bg-black/90 transition-colors disabled:opacity-40 flex items-center justify-center shrink-0"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================
// ChatPage
// ============================================================
const ChatPage = () => {
  const { user, userData } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const withUserId = searchParams.get('with');

  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ---------- Load all conversations for this client ----------
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'messages'),
      where('clientId', '==', user.uid)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      const map = new Map();
      all.forEach(msg => {
        const proId = msg.professionalId;
        if (!proId) return;
        if (!map.has(proId)) {
          map.set(proId, {
            professionalId: proId,
            professionalName: msg.professionalName || 'Professional',
            professionalImage: msg.professionalImage || null,
            messages: [],
          });
        }
        map.get(proId).messages.push(msg);
      });

      const summaries = Array.from(map.values()).map(conv => {
        conv.messages.sort((a, b) => {
          const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
          const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
          return bT - aT;
        });
        const latest = conv.messages[0];
        const unread = conv.messages.filter(
          m => m.sender === 'professional' && !m.read
        ).length;
        return {
          professionalId: conv.professionalId,
          professionalName: conv.professionalName,
          professionalImage: conv.professionalImage,
          lastMessage: latest?.text || '',
          lastTimestamp: latest?.createdAt,
          lastSender: latest?.sender,
          unread,
        };
      });

      summaries.sort((a, b) => {
        const aT = a.lastTimestamp?.toMillis?.() ?? 0;
        const bT = b.lastTimestamp?.toMillis?.() ?? 0;
        return bT - aT;
      });

      setConversations(summaries);
      setLoading(false);
    }, (err) => {
      console.error('Chat list snapshot error:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [user?.uid]);

  // ---------- If ?with=proId, open (or create) that conversation ----------
  useEffect(() => {
    if (!withUserId || !user?.uid) return;

    const existing = conversations.find(c => c.professionalId === withUserId);
    if (existing) {
      setActiveChat(existing);
      return;
    }

    const loadPro = async () => {
      setCreating(true);
      try {
        const proSnap = await getDoc(doc(db, 'users', withUserId));
        if (proSnap.exists()) {
          const pro = proSnap.data();
          setActiveChat({
            professionalId: withUserId,
            professionalName: pro.businessName || pro.name || 'Professional',
            professionalImage: pro.profileImage || pro.coverImage || null,
            lastMessage: '',
            lastTimestamp: null,
            lastSender: null,
            unread: 0,
            isNew: true,
          });
        }
      } catch (err) {
        console.error('Failed to load pro for chat:', err);
      } finally {
        setCreating(false);
      }
    };

    loadPro();
  }, [withUserId, user?.uid, conversations]);

  // ---------- Stream messages for the active chat ----------
  useEffect(() => {
    if (!activeChat?.professionalId || !user?.uid) {
      setActiveMessages([]);
      return;
    }

    const q = query(
      collection(db, 'messages'),
      where('clientId', '==', user.uid),
      where('professionalId', '==', activeChat.professionalId)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      msgs.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return aT - bT;
      });
      setActiveMessages(msgs);

      msgs.forEach(m => {
        if (m.sender === 'professional' && !m.read) {
          updateDoc(doc(db, 'messages', m.id), { read: true }).catch(() => {});
        }
      });
    });

    return () => unsub();
  }, [activeChat?.professionalId, user?.uid]);

  // ---------- Auto-scroll ----------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages.length]);

  // ---------- Send ----------
  const sendMessage = useCallback(async () => {
    const text = newMessage.trim();
    if (!text || !activeChat?.professionalId || sending) return;

    setSending(true);
    try {
      const ids = [user.uid, activeChat.professionalId].sort();
      const conversationId = `${ids[0]}_${ids[1]}`;

      await addDoc(collection(db, 'messages'), {
        conversationId,
        clientId: user.uid,
        clientName: userData?.name || user.displayName || 'Client',
        clientImage: userData?.profileImage || user.photoURL || null,
        professionalId: activeChat.professionalId,
        professionalName: activeChat.professionalName || 'Professional',
        professionalImage: activeChat.professionalImage || null,
        sender: 'client',
        text,
        read: false,
        createdAt: serverTimestamp(),
      });

      setNewMessage('');
      inputRef.current?.focus();

      if (withUserId) {
        setSearchParams({}, { replace: true });
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  }, [newMessage, activeChat, sending, user, userData, withUserId, setSearchParams]);

  // ---------- Filtered conversation list ----------
  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversations;
    const s = searchTerm.toLowerCase();
    return conversations.filter(c =>
      c.professionalName?.toLowerCase().includes(s) ||
      c.lastMessage?.toLowerCase().includes(s)
    );
  }, [conversations, searchTerm]);

  // ---------- Loading ----------
  if (loading || creating) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const onBack = () => {
    setActiveChat(null);
    if (withUserId) setSearchParams({}, { replace: true });
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
      {/* ============================================================ */}
      {/* DESKTOP VIEW */}
      {/* ============================================================ */}
      <div className="hidden lg:block max-w-6xl mx-auto h-[calc(100vh-200px)]">
        <div className="bg-white rounded-3xl border border-gray-100 h-full flex overflow-hidden">
          {/* Conversation List */}
          <div className="w-1/3 border-r border-gray-50 flex flex-col">
            <div className="p-4 border-b border-gray-50">
              <h2 className="text-xl font-serif font-bold text-gray-900 mb-3">Messages</h2>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-10 pr-4 py-2.5 bg-[#FCF8F9] border border-gray-100 rounded-xl text-sm focus:outline-none focus:border-[#EFA9B6]"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <MessageCircle size={32} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">
                    {conversations.length === 0 ? 'No conversations yet' : 'No matches'}
                  </p>
                </div>
              ) : (
                filteredConversations.map((chat) => (
                  <button
                    key={chat.professionalId}
                    onClick={() => {
                      setActiveChat(chat);
                      if (withUserId) setSearchParams({}, { replace: true });
                    }}
                    className={`w-full text-left p-4 hover:bg-[#FCF8F9] transition-colors border-b border-gray-50 flex items-center gap-3 ${
                      activeChat?.professionalId === chat.professionalId ? 'bg-[#FFF5F6]' : ''
                    }`}
                  >
                    <div className="relative shrink-0">
                      <div className="w-12 h-12 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] overflow-hidden">
                        {chat.professionalImage ? (
                          <img src={chat.professionalImage} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User size={20} />
                        )}
                      </div>
                      {chat.unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#EFA9B6] text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                          {chat.unread > 9 ? '9+' : chat.unread}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-0.5">
                        <h3 className={`text-sm truncate ${chat.unread > 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
                          {chat.professionalName}
                        </h3>
                        <span className={`text-[10px] shrink-0 ${chat.unread > 0 ? 'text-[#EFA9B6] font-bold' : 'text-gray-400'}`}>
                          {formatRelative(chat.lastTimestamp)}
                        </span>
                      </div>
                      <p className={`text-xs truncate ${chat.unread > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                        {chat.lastSender === 'client' ? 'You: ' : ''}
                        {chat.lastMessage || 'No messages yet'}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Window */}
          <div className="flex-1 flex flex-col bg-white">
            {activeChat ? (
              <ChatThread
                activeChat={activeChat}
                activeMessages={activeMessages}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                sending={sending}
                sendMessage={sendMessage}
                inputRef={inputRef}
                messagesEndRef={messagesEndRef}
                onBack={null}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle size={32} className="text-[#EFA9B6]" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">
                    No conversation selected
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Choose a conversation from the list to start chatting
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* MOBILE VIEW */}
      {/* ============================================================ */}
      <div className="lg:hidden -mx-4 -my-6 h-[calc(100vh-120px)] flex flex-col bg-[#FCF8F9]">
        {!activeChat ? (
          <>
            <div className="px-6 pt-8 pb-4 flex justify-between items-center shrink-0"></div>
            <div className="px-6 pb-4 shrink-0">
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-11 pr-4 py-3 bg-white border border-gray-100 rounded-2xl text-sm focus:outline-none focus:border-[#EFA9B6]"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-24 space-y-2">
              {filteredConversations.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle size={32} className="text-[#EFA9B6]" />
                  </div>
                  <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
                    {conversations.length === 0 ? 'No conversations yet' : 'No matches'}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {conversations.length === 0
                      ? 'Message a pro from their profile to start'
                      : 'Try a different search term'}
                  </p>
                </div>
              ) : (
                filteredConversations.map((chat) => (
                  <button
                    key={chat.professionalId}
                    onClick={() => setActiveChat(chat)}
                    className="w-full text-left bg-white rounded-3xl p-4 border border-gray-100 hover:border-[#EFA9B6] transition-colors flex items-center gap-4"
                  >
                    <div className="relative shrink-0">
                      <div className="w-14 h-14 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] overflow-hidden">
                        {chat.professionalImage ? (
                          <img src={chat.professionalImage} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User size={22} />
                        )}
                      </div>
                      {chat.unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 bg-[#EFA9B6] text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                          {chat.unread > 9 ? '9+' : chat.unread}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-0.5">
                        <h3 className={`text-[15px] truncate ${chat.unread > 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
                          {chat.professionalName}
                        </h3>
                        <span className={`text-[10px] shrink-0 ${chat.unread > 0 ? 'text-[#EFA9B6] font-bold' : 'text-gray-400'}`}>
                          {formatRelative(chat.lastTimestamp)}
                        </span>
                      </div>
                      <p className={`text-xs truncate ${chat.unread > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                        {chat.lastSender === 'client' ? 'You: ' : ''}
                        {chat.lastMessage || 'No messages yet'}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            <ChatThread
              activeChat={activeChat}
              activeMessages={activeMessages}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              sending={sending}
              sendMessage={sendMessage}
              inputRef={inputRef}
              messagesEndRef={messagesEndRef}
              onBack={onBack}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ChatPage;